import { ReactNode } from 'react';
import { CenteredSpinner } from '~/commonComponents/CenteredSpinner';
import { shouldUseDsoStructure } from '~/growthbook';
import { useAppSelector } from '~/hooks/useAppSelector';
import { useViewedOfficeScope } from '~/hooks/useViewedOfficeScope';
import { getViewedOfficeId } from '~/utils/viewedOfficeScope';
import { NotAllowedGuard } from './NotAllowedGuard';

type TLocationAccess = 'allowed' | 'loading' | 'refused';

/**
 * Whether the signed-in account holds `officeId`: its own office or one of its
 * locations. The locations come with `/auth/me`. Under the DSO structure they would
 * come into `offices`, which nothing loads since `GET_CHILD_OFFICES_ACTION` lost its
 * dispatcher, so with that list empty there is nothing to check against: the page
 * loads, and the server refuses an office the account does not hold, as before.
 */
const useLocationAccess = (officeId: string | undefined): TLocationAccess => {
  const signedInOfficeId = useAppSelector((state) => state.user.user?.id);
  const childOffices = useAppSelector((state) => state.user.childOffices);
  const offices = useAppSelector((state) => state.user.offices);

  if (!officeId || officeId === signedInOfficeId) return 'allowed';
  if ([...(childOffices ?? []), ...(offices ?? [])].some((office) => office.id === officeId)) {
    return 'allowed';
  }
  if (!signedInOfficeId) return 'loading';
  if (shouldUseDsoStructure() && !offices?.length) return 'allowed';

  return 'refused';
};

/**
 * The gate for a page a Manage row opens for one location (`?officeId=`,
 * GOTU-19472). An id the account does not hold, such as a hand-typed or stale
 * link, goes back to the dashboard with the not-allowed popup before the page
 * renders, so no request names another account's office. Any other scope renders
 * the page, which waits on the account split flag itself.
 */
export const ViewedLocationRoute = ({ children }: { children: ReactNode }) => {
  const scope = useViewedOfficeScope();
  const access = useLocationAccess(getViewedOfficeId(scope));

  if (access === 'loading') return <CenteredSpinner />;

  if (access === 'refused') {
    return (
      <NotAllowedGuard customCondition notAllowedReason="location-access">
        {null}
      </NotAllowedGuard>
    );
  }

  return <>{children}</>;
};
